import VisibilityIcon from "@mui/icons-material/Visibility";

import { useAuth, useSystemUser } from "@/providers/auth/hooks";
import { useOrg } from "@/shared/domain/orgs/hooks";
import { useViewingResourceOrg } from "@/shared/services";

import styles from "./CheckOutOrgButton.module.css";
import { RobotoIconButton } from "./RobotoIconButton";
import { RobotoTooltip } from "./RobotoTooltip";

/**
 * The eyeball icon in the top bar, beside the support/notifications/theme
 * toggles: when a Roboto system user follows a link into a resource owned by a
 * *different* org (datasets, files, the visualizer, and the detail pages that
 * register an org viewing context), this offers a one-click "View as {org}"
 * that makes that org the active org via `setCurrentOrganization` — the same
 * switch the admin "View As" button performs, in the same spot the
 * per-page eyeball buttons used to occupy.
 *
 * It renders nothing unless there's something to act on:
 * - hidden when the viewed org can't be resolved to a single org (nothing
 *   registered, record not yet loaded, or a mix of orgs — see
 *   {@link useViewingResourceOrg});
 * - hidden when the viewed org already matches the active org (nothing to
 *   switch to);
 * - hidden for non-system users (gated by the `isSystemUser` check in
 *   `shouldOffer`; they would be rejected by the backend anyway, so the org
 *   fetch is also skipped to avoid a guaranteed 403, mirroring
 *   `ViewAsOrgButton`).
 */
export function CheckOutOrgButton() {
  const { currentOrganization, setCurrentOrganization } = useAuth();
  const { isSystemUser } = useSystemUser();
  const viewedOrgId = useViewingResourceOrg();

  const aligned =
    viewedOrgId !== null && viewedOrgId === currentOrganization?.org_id;

  // Only resolve the org when the button could actually render. Skipping the
  // fetch for non-system users avoids a guaranteed 403.
  const shouldOffer = isSystemUser && viewedOrgId !== null && !aligned;
  const orgQuery = useOrg(shouldOffer ? viewedOrgId : undefined);
  const targetOrg = orgQuery.data;

  if (!shouldOffer || !targetOrg) {
    return null;
  }

  const orgLabel = targetOrg.name || targetOrg.org_id;

  return (
    <RobotoTooltip title={`View as ${orgLabel} (${targetOrg.org_id})`}>
      <span>
        <RobotoIconButton
          aria-label={`View as ${orgLabel}`}
          onClick={() => setCurrentOrganization(targetOrg)}
        >
          <VisibilityIcon fontSize="small" className={styles.alertIcon} />
        </RobotoIconButton>
      </span>
    </RobotoTooltip>
  );
}
